import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

let openModalCount = 0;
let savedBodyOverflow: string | undefined;
let nextModalId = 0;
const modalStack: number[] = [];

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => el.tabIndex !== -1);
}

function getModalFocusables(dialog: HTMLElement): HTMLElement[] {
  const focusables = getFocusableElements(dialog);
  const dialogId = dialog.id;
  if (!dialogId) {
    return focusables;
  }

  const portalFocusables = Array.from(
    document.querySelectorAll<HTMLElement>(
      `[data-modal-portal="${CSS.escape(dialogId)}"]`,
    ),
  ).flatMap((portal) => getFocusableElements(portal));
  return [...focusables, ...portalFocusables];
}

function focusInitialElement(container: HTMLElement | null): void {
  if (!container) {
    return;
  }
  const focusable = getModalFocusables(container);
  if (focusable.length > 0) {
    focusable[0].focus();
    return;
  }
  container.focus();
}

function trapFocus(event: KeyboardEvent, container: HTMLElement): void {
  if (event.key !== "Tab") {
    return;
  }

  const focusable = getModalFocusables(container);
  if (focusable.length === 0) {
    event.preventDefault();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey) {
    if (active === first || !container.contains(active)) {
      event.preventDefault();
      last.focus();
    }
  } else if (active === last) {
    event.preventDefault();
    first.focus();
  }
}

function isTopModal(modalId: number): boolean {
  return modalStack[modalStack.length - 1] === modalId;
}

export function useModalAccessibility({
  isOpen,
  onClose,
  closeOnEscape,
  dialogRef,
}: {
  isOpen: boolean;
  onClose: () => void;
  closeOnEscape: boolean;
  dialogRef: RefObject<HTMLElement | null>;
}): void {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const dialogRefRef = useRef(dialogRef);

  onCloseRef.current = onClose;
  closeOnEscapeRef.current = closeOnEscape;
  dialogRefRef.current = dialogRef;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const modalId = ++nextModalId;
    modalStack.push(modalId);

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    openModalCount += 1;
    if (openModalCount === 1) {
      savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    const focusFrame = requestAnimationFrame(() => {
      focusInitialElement(dialogRefRef.current.current);
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopModal(modalId)) {
        return;
      }

      const dialog = dialogRefRef.current.current;
      if (!dialog) {
        return;
      }

      if (event.key === "Escape") {
        if (!closeOnEscapeRef.current) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key === "Tab") {
        trapFocus(event, dialog);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown, true);

      const stackIndex = modalStack.lastIndexOf(modalId);
      if (stackIndex !== -1) {
        modalStack.splice(stackIndex, 1);
      }

      openModalCount -= 1;
      if (openModalCount === 0) {
        document.body.style.overflow = savedBodyOverflow ?? "";
        savedBodyOverflow = undefined;
      }

      const previous = previouslyFocusedRef.current;
      if (previous?.isConnected) {
        previous.focus();
      }
    };
  }, [isOpen]);
}
